import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ClipboardList,
  RefreshCw,
  Scale,
  CloudCog,
} from "lucide-react";
import Footer from "../../../components/Footer";

/* ---------------- DATA DEFINITIONS ---------------- */

const HERO = {
  title: "Governance, Risk & Assurance",
  subtitle:
    "Governance, Risk & Assurance is no longer about policies, spreadsheets, or passing audits once a year.",
};

const REALITY_CARDS = [
  {
    title: "Governance exists on paper, not in practice",
    desc: "Policies are written, but ownership and accountability are unclear.",
    icon: ShieldCheck,
  },
  {
    title: "Risk is assessed periodically, not continuously",
    desc: "Risk registers are updated for audits, while exposure changes daily.",
    icon: RefreshCw,
  },
  {
    title: "Compliance is reactive and disruptive",
    desc: "Audits trigger last-minute firefighting and distraction.",
    icon: ClipboardList,
  },
  {
    title: "Multiple frameworks create duplicated effort",
    desc: "ISO, SOC 2, and laws overlap — yet teams rebuild controls.",
    icon: Scale,
  },
  {
    title: "Cloud and AI outpace traditional models",
    desc: "Static governance models fail to keep up with automated environments.",
    icon: CloudCog,
  },
];

const COMPLIANCE_LOGOS = [
  {
    name: "CCPA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/CCPA-Compliant.svg",
  },
  {
    name: "GDPR",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/GDPR-Compliant.svg",
  },
  {
    name: "HIPAA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/HIPAA-Compliant.svg",
  },
  {
    name: "HITRUST",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/HITRUST.svg",
  },
  {
    name: "ISO 27001",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/ISO-27001.svg",
  },
  {
    name: "PCI DSS",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/PCI-DSS-Compliant.svg",
  },
  {
    name: "SOC 2",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/SOC-2.svg",
  },
  {
    name: "FedRAMP",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/FR-FedRamp.svg",
  },
  {
    name: "NIST",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/NIST.svg",
  },
  {
    name: "DORA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/Dora.svg",
  },
];

/* ---------------- SUB-COMPONENTS ---------------- */

const RealityCard = ({ card }) => (
  <motion.div
    whileHover={{ y: -5 }}
    className="bg-[#032d28] p-8 rounded-[24px] border border-emerald-500/10 flex flex-col min-h-[280px] transition-all group hover:border-[#24ff81]/30"
  >
    <div className="mb-6 p-3 bg-emerald-500/10 rounded-xl w-fit group-hover:bg-[#24ff81]/20">
      <card.icon className="text-[#24ff81]" size={24} />
    </div>
    <h3 className="text-white text-lg font-bold mb-4 uppercase tracking-tight">
      {card.title}
    </h3>
    <p className="text-emerald-100/60 text-sm leading-relaxed font-medium">
      {card.desc}
    </p>
  </motion.div>
);

const InfoSection = ({ title, why, approach, deliverables, isDarkGreen }) => {
  const bgColor = isDarkGreen ? "bg-[#032d28]" : "bg-white";
  const titleColor = isDarkGreen ? "text-[#24ff81]" : "text-[#032d28]";
  const textColor = isDarkGreen ? "text-white" : "text-[#032d28]";
  const lineColor = isDarkGreen ? "bg-white/50" : "bg-[#032d28]";

  return (
    <section className={`${bgColor} py-24`}>
      <div className="max-w-7xl mx-auto px-10 md:px-20 text-left">
        <h2
          className={`text-5xl md:text-7xl font-bold uppercase mb-20 tracking-tighter ${titleColor}`}
        >
          {title}
        </h2>

        <div className={`h-[1px] w-full ${lineColor}`} />
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] lg:grid-cols-[300px_1fr] gap-8 py-12">
          <span
            className={`font-bold text-sm tracking-widest uppercase ${textColor} opacity-60`}
          >
            Why
          </span>
          <div className="max-w-4xl space-y-6">
            {why.map((para, i) => (
              <p
                key={i}
                className={`text-xl md:text-2xl font-medium leading-relaxed ${textColor}`}
              >
                {para}
              </p>
            ))}
          </div>
        </div>

        <div className={`h-[1px] w-full ${lineColor} `} />
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] lg:grid-cols-[300px_1fr] gap-8 py-12">
          <span
            className={`font-bold text-sm tracking-widest uppercase ${textColor} opacity-60`}
          >
            Our Approach
          </span>
          <div className="max-w-4xl space-y-6">
            {approach.map((para, i) => (
              <p
                key={i}
                className={`text-lg md:text-xl font-medium leading-relaxed opacity-90 ${textColor}`}
              >
                {para}
              </p>
            ))}
          </div>
        </div>

        <div className={`h-[1px] w-full ${lineColor}`} />
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] lg:grid-cols-[300px_1fr] gap-8 py-12">
          <span
            className={`font-bold text-sm tracking-widest uppercase ${textColor} opacity-60`}
          >
            What We Deliver
          </span>
          <div className="space-y-6">
            {deliverables.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <span
                  className={`mt-2.5 w-1.5 h-1.5 rounded-full shrink-0 ${isDarkGreen ? "bg-[#24ff81]" : "bg-[#032d28]"}`}
                />
                <p
                  className={`text-lg font-medium leading-relaxed ${textColor}`}
                >
                  <span className="font-black uppercase">{item.t}</span> —{" "}
                  {item.d}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className={`h-[1px] w-full ${lineColor}`} />
      </div>
    </section>
  );
};

const CertificationSlider = () => {
  return (
    <section className="bg-[#ffffff] py-32 overflow-hidden">
      <div className="container mx-auto px-10 md:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-24">
          <div className="lg:col-span-8">
            <h2 className="text-[#043630] text-4xl md:text-5xl font-bold tracking-tighter leading-[0.9] uppercase">
              Compliance that keeps up with your customers.
            </h2>
          </div>

          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <a
              href="/Contact"
              className="inline-block bg-[#ccff00] hover:bg-[#b5e600] text-[#032d28] text-lg font-black px-12 py-6 rounded-full uppercase transition-all hover:scale-105 active:scale-95 shadow-xl shadow-lime-400/20 text-center no-underline"
            >
              Get started
            </a>
          </div>
        </div>

        <div className="relative mt-12">
          <motion.div
            className="flex gap-20 items-center"
            animate={{ x: [0, -2000] }}
            transition={{ repeat: Infinity, duration: 45, ease: "linear" }}
          >
            {[...COMPLIANCE_LOGOS, ...COMPLIANCE_LOGOS].map((cert, i) => (
              <div
                key={i}
                className="flex flex-col items-center min-w-[140px] grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100"
              >
                <img
                  src={cert.url}
                  alt={cert.name}
                  className="w-24 h-24 md:w-32 md:h-32 object-contain mb-6"
                />
                <span className="text-[#032d28] text-xs font-black tracking-widest uppercase text-center">
                  {cert.name}
                </span>
              </div>
            ))}
          </motion.div>

          <div className="w-full max-w-[800px] mx-auto mt-28">
            <div className="h-[2px] w-full bg-[#032d28]/10 relative rounded-full">
              <motion.div
                className="absolute top-[-2px] h-[6px] bg-[#032d28] rounded-full w-[60px]"
                animate={{ left: ["0%", "92%", "0%"] }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

export default function GovernanceRiskCompliance() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Governance, Risk & Assurance Services | Synthera Security</title>

        <meta
          name="description"
          content="Strengthen governance, manage cyber risk, and achieve continuous compliance with Synthera Security's Governance, Risk & Assurance services."
        />

        <meta
          name="keywords"
          content="Governance Risk Assurance, GRC, Risk Management, Security Assurance, Compliance Management, ISO 27001, PCI DSS, Cyber Risk, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Governance, Risk & Assurance Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Manage cyber risk, validate security controls, and achieve compliance with expert Governance, Risk & Assurance services."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/governance-risk-compliance"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/governance-risk-compliance"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans overflow-hidden">
        {/* --- HERO --- */}
        <section className="bg-[#032d28] pt-40 pb-32 px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]" />
          <div className="container mx-auto max-w-5xl text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-[#24ff81] text-xs tracking-[0.3em] uppercase font-bold mb-6 block">
                Continuous Assurance
              </span>
              <h1 className="text-white text-4xl md:text-7xl font-bold leading-[1.1] mb-10 tracking-tight">
                {HERO.title}
              </h1>
              <div className="w-24 h-1 bg-[#24ff81] mx-auto mb-10" />
              <p className="text-emerald-100/90 text-2xl md:text-3xl max-w-4xl mx-auto font-bold">
                {HERO.subtitle}
              </p>
            </motion.div>
          </div>
        </section>

        {/* --- REALITY CARDS --- */}
        <section className="py-24 px-6 bg-white">
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-[#032d28] text-3xl font-black uppercase tracking-tighter mb-16 text-center">
              The Reality Organizations Face Today
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {REALITY_CARDS.map((card, i) => (
                <RealityCard key={i} card={card} />
              ))}
            </div>
          </div>
        </section>

        {/* --- SECTION 1: RISK MANAGEMENT --- */}
        <InfoSection
          title="Risk Management"
          isDarkGreen={true}
          why={[
            "Most organisations don’t lack security tools — they lack clarity on risk.",
            "Security decisions are often reactive, driven by alerts, audits, or vendor pressure rather than a clear understanding of what could materially impact the business.",
            "Our Risk Management service helps organisations identify, quantify, and prioritise cyber risk in business terms, enabling leadership to make defensible decisions on investment, acceptance, and mitigation.",
          ]}
          approach={[
            "We take a business-first risk approach, not a checklist-driven one. By combining threat modeling, control maturity analysis, and exposure data, we translate technical risk into financial, operational, and regulatory impact.",
            "We act as an extension of your leadership and security teams, ensuring risk is continuously assessed, clearly communicated, and actively managed — not buried in reports.",
          ]}
          deliverables={[
            {
              t: "Enterprise Risk Identification",
              d: "Structured identification of cyber risks across people, processes, technology, and third parties aligned to business objectives.",
            },
            {
              t: "Risk Quantification & Prioritisation",
              d: "Clear assessment of likelihood and impact, translating technical threats into business-relevant risk levels and decision-ready insights.",
            },
            {
              t: "Risk Treatment Strategy",
              d: "Defined mitigation, transfer, acceptance, or avoidance strategies mapped to ownership, timelines, and cost-benefit rationale.",
            },
            {
              t: "Executive Risk Reporting",
              d: "C-suite and board-ready dashboards that show where the organisation is exposed, what is improving, and what requires leadership action.",
            },
          ]}
        />

        {/* --- SECTION 2: SECURITY ASSURANCE --- */}
        <InfoSection
          title="Security Assurance"
          isDarkGreen={false}
          why={[
            "Having security controls in place doesn’t mean they actually work. Many organisations discover control failures only during incidents, audits, or breaches when it’s already too late.",
            "Security Assurance provides independent validation that your security controls, detection mechanisms, and response capabilities operate effectively in real-world conditions.",
          ]}
          approach={[
            "We validate security from an outcome perspective, not policy intent. Controls are tested against realistic threat scenarios to confirm effectiveness, coverage, and operational readiness.",
            "We focus on assurance that stands up to scrutiny from regulators, customers, and internal audit not theoretical compliance.",
          ]}
          deliverables={[
            {
              t: "Control Effectiveness Validation",
              d: "Testing of technical and operational controls to verify they perform as designed, identifying gaps between policy and practice.",
            },
            {
              t: "Vulnerability & Exposure Assurance",
              d: "Assessment of exploitable weaknesses across endpoints, cloud platforms, networks, and identities tied directly to control failures.",
            },
            {
              t: "Incident Readiness Assurance",
              d: "Evaluation of detection, response, escalation, and recovery capabilities through scenario based testing and tabletop exercises.",
            },
            {
              t: "Third-Party Security Assurance",
              d: "Independent validation of vendor and partner security postures to reduce supply chain and ecosystem risk.",
            },
          ]}
        />

        {/* --- SECTION 3: AUTOMATED COMPLIANCE --- */}
        <InfoSection
          title="Automated Compliance"
          isDarkGreen={true}
          why={[
            "Compliance is no longer optional it directly impacts revenue, customer trust, and market access. However, many organisations struggle with fragmented compliance efforts that are manual, audit-driven, and unsustainable.",
            "Our Compliance service enables organisations to achieve and maintain regulatory and industry certifications while ensuring controls are practical, auditable, and operationally sound.",
          ]}
          approach={[
            "We deliver end-to-end compliance program management, combining structured framework alignment with automation and hands-on engineering support.",
            "Rather than treating compliance as a periodic event, we embed it into daily operations, acting as your internal compliance function and preparing you for external audits with minimal disruption.",
            "We support frameworks including ISO 27001, PCI-DSS, and other regulatory requirements relevant to your industry.",
          ]}
          deliverables={[
            {
              t: "Compliance Gap Analysis",
              d: "Detailed assessment of current-state controls against certification requirements with a prioritised remediation roadmap.",
            },
            {
              t: "Control Mapping & Evidence Alignment",
              d: "Clear traceability between controls, policies, technical implementations, and audit evidence.",
            },
            {
              t: "Automated Compliance Monitoring",
              d: "Continuous compliance tracking and reporting using integrated risk and compliance platforms.",
            },
            {
              t: "Certification & Audit Support",
              d: "End-to-end support during external audits, including auditor coordination, evidence preparation, and certification readiness.",
            },
          ]}
        />

        {/* --- CERTIFICATION SLIDER --- */}
        <CertificationSlider />

        <Footer />
      </div>
    </>
  );
}
